<template>
  <div class="ec-list">

    <admin-table-subheader
      v-model="search"
      title="Licences"
      search
      search-placeholder="Search licence…"
      :refresh-button="true"
      :refresh-loading="licenceLoading"
      :secondary-buttons="readonly ? [] : [
        { label: 'Role Assets',      icon: 'mdi-plus',   click: roleAsset },
        { label: 'Licence Scopes',   icon: 'mdi-target', click: openCustomerLicenceDialog }
      ]"
      :primary-button="readonly ? null : { label: 'New Licence', icon: 'mdi-plus', click: openNewLicenceDialog }"
      @refresh="initialize"
    />

    <div v-if="licenceLoading" class="ec-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="details"
      :search="search"
      :items-per-page="30"
      :footer-props="{ 'items-per-page-options': [10, 30, 50, -1] }"
      class="ec-table"
    >
      <template v-slot:[`item.licence_name`]="{ item }">
        <code class="lc-name">{{ item.licence_name }}</code>
      </template>

      <template v-slot:[`item.group_id`]="{ item }">
        <span v-if="item.group_id && parentLicenceMap[item.group_id]" class="ec-badge ec-badge--purple">
          <v-icon x-small style="margin-right:4px">{{ parentLicenceMap[item.group_id].icon || 'mdi-folder-outline' }}</v-icon>
          {{ parentLicenceMap[item.group_id].label }}
        </span>
        <span v-else class="u-status-pill u-pill--gray">—</span>
      </template>

      <template v-slot:[`item.is_active`]="{ item }">
        <span class="u-status-pill" :class="item.is_active ? 'u-pill--green' : 'u-pill--red'">
          <v-icon size="11" style="margin-right:3px">{{ item.is_active ? 'mdi-check' : 'mdi-close' }}</v-icon>
          {{ item.is_active ? 'Active' : 'Passive' }}
        </span>
      </template>

      <template v-slot:[`item.deleted`]="{ item }">
        <span class="u-status-pill" :class="item.deleted ? 'u-pill--red' : 'u-pill--gray'">
          <v-icon size="11" style="margin-right:3px">{{ item.deleted ? 'mdi-check' : 'mdi-minus' }}</v-icon>
          {{ item.deleted ? 'Yes' : 'No' }}
        </span>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        <span class="u-date">{{ timeParserDate(item.created_at) }}</span>
      </template>

      <template v-slot:[`item.started`]="{ item }">
        <span class="u-date">{{ timeParserDate(item.started) }}</span>
      </template>

      <template v-slot:[`item.price_monthly`]="{ item }">
        <span class="u-date">{{ item.price_monthly }} $</span>
      </template>

      <template v-slot:[`item.price_yearly`]="{ item }">
        <span class="u-date">{{ item.price_yearly }} $</span>
      </template>

      <template v-slot:[`item.features`]="{ item }">
        <button class="u-inline-btn" @click="getFeatureLimit(item)">
          <v-icon size="13" style="margin-right:4px">mdi-eye-outline</v-icon>
          Features
        </button>
      </template>

      <template v-slot:[`item.available_slots`]="{ item }">
        <template v-if="poolMap[item.id]">
          <span
            class="u-status-pill"
            :class="poolMap[item.id].available_slots > 0 ? 'u-pill--green' : 'u-pill--red'"
          >
            {{ poolMap[item.id].available_slots }} / {{ poolMap[item.id].total_slots }}
          </span>
        </template>
        <span v-else class="u-status-pill u-pill--gray">—</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <template v-if="!readonly">
          <button class="ec-icon-btn ec-icon-btn--primary" title="Edit" @click="edit_licence(item)">
            <v-icon size="15">mdi-pencil-outline</v-icon>
          </button>
          <button v-if="!item.deleted" class="ec-icon-btn ec-icon-btn--danger" title="Delete" @click="delete_licence(item)">
            <v-icon size="15">mdi-delete-outline</v-icon>
          </button>
        </template>
        <span v-else class="u-status-pill u-pill--gray">View only</span>
      </template>
    </v-data-table>

    <!-- ── Dialogs (logic unchanged) ────────────────────────────────────── -->
    <new-licence
      v-if="showNewLicenceDialog"
      :newLicenceDialog="showNewLicenceDialog"
      @closeNewLicenceDialog="showNewLicenceDialog = false"
      @getAllLicences="allLicences"
    />

    <edit-licence-form
      v-if="showEditLicenceDialog"
      @closeEditLicenceDialog="closeEditLicenceDialog"
      :showEditLicenceDialog="showEditLicenceDialog"
      :licenceInfoForEdit="editLicenceProp"
    />

    <licence-key-feature
      v-if="showLicenceFeatures"
      :showLicenceFeaturesDialog="showLicenceFeatures"
      :features="features"
      :licenceName="licenceName"
      @closeLicenceKeyFeature="showLicenceFeatures = false"
    />

    <licence-scopes
      v-if="showLicenceScopeDialog"
      :scopesDialog="showLicenceScopeDialog"
      @closeSheet="closeSheet"
    />

    <role-assets
      v-if="showRoleAssetsDialog"
      :assetDialog="showRoleAssetsDialog"
      @closeSheet="showRoleAssetsDialog = false"
    />
  </div>
</template>

<script>
import converter from '@/mixins/converter'
import LicenceGenerate from './LicenceGenerate.vue'
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import LicenceFeatures from './LicenceFeatures.vue'
import LicenceAssign from './LicenceAssign.vue'
import NewLicence from './NewLicence.vue'
import LicenceKeyFeature from '../features/LicenceKeyFeature.vue'
import EditLicenceForm from './EditLicenceForm.vue'
import LicenceScopes from './LicenceScopes.vue'
import RoleAssets from '../role_assets/RoleAssets.vue'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'

export default converter.extend({
  components: {
    LicenceGenerate,
    LicenceFeatures,
    LicenceAssign,
    NewLicence,
    LicenceKeyFeature,
    EditLicenceForm,
    LicenceScopes,
    RoleAssets,
    AdminTableSubheader
  },
  props: {
    assignAction: { type: Object },
    isAssign: { type: Boolean },
    user: { type: Object },
    readonly: { type: Boolean, default: false },
    pools: { type: Array, default: () => [] }
  },
  data () {
    return {
      details: [],
      features: [],
      editLicenceProp: {},
      licenceName: '',
      licenceLoading: false,
      search: null,
      showLicenceModal: false,
      showEditLicenceDialog: false,
      showLicenceFeatures: false,
      showLicenceScopeDialog: false,
      showNewLicenceDialog: false,
      showRoleAssetsDialog: false,
      parentLicences: []
    }
  },
  computed: {
    parentLicenceMap () {
      const map = {}
      this.parentLicences.forEach((p) => { map[p.id] = p })
      return map
    },
    poolMap () {
      const map = {}
      this.pools.forEach((p) => { map[p.licence_type_id] = p })
      return map
    },
    headers () {
      const base = [
        { text: 'Name', value: 'licence_name', align: 'start' },
        { text: 'Group', value: 'group_id', align: 'center' },
        { text: 'Active', value: 'is_active', align: 'center' },
        { text: 'Deleted', value: 'deleted', align: 'center' },
        { text: 'Created', value: 'created_at', align: 'center' },
        { text: 'Monthly Price', value: 'price_monthly', align: 'center' },
        { text: 'Yearly Price', value: 'price_yearly', align: 'center' },
        { text: 'Features', value: 'features', align: 'center', sortable: false }
      ]
      if (this.readonly) {
        base.push({ text: 'Available Slots', value: 'available_slots', align: 'center', sortable: false })
      }
      base.push({ text: 'Actions', value: 'actions', align: 'center', sortable: false })
      return base
    }
  },
  mounted () {
    this.initialize()
    this.loadParentLicences()
  },
  methods: {
    roleAsset () { this.showRoleAssetsDialog = true },

    async loadParentLicences () {
      try {
        const { data } = await apiService.get('admin-parent-licences')
        this.parentLicences = data.data || []
      } catch (err) {
        console.error('Error loading parent licences:', err)
      }
    },

    async initialize () {
      this.licenceLoading = true
      this.allLicences()
    },

    assignLicence (item) {
      this.editLicenceProp = item
      this.showLicenceModal = true
    },

    async allLicences () {
      await apiService.get('admin-get-licences')
        .then((res) => { this.details = res.data.data })
        .finally(() => (this.licenceLoading = false))
        .catch((err) => { console.log('ERR', err.response) })
    },

    async edit_licence (item) {
      this.showEditLicenceDialog = true
      this.editLicenceProp = item
      await this.$store.dispatch('getLicenceScopes')
    },

    getFeatureLimit (item) {
      this.licenceName = item.licence_name
      this.features = item.features
      this.showLicenceFeatures = true
    },

    closeEditLicenceDialog () {
      this.showEditLicenceDialog = false
      this.allLicences()
    },

    async delete_licence (licence) {
      Swal.fire({
        title: 'Are you sure?',
        text: "The license will be permanently deleted. You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.value) {
          await apiService.get(`admin-delete-permanently-licence/${licence.id}`)
            .catch((err) => { Swal.showValidationMessage(`Request failed: ${err}`) })
          this.allLicences()
          Swal.fire('Deleted!', 'Licence has been deleted.', 'success')
        }
      })
    },

    async openCustomerLicenceDialog () {
      this.showLicenceScopeDialog = true
      await this.$store.dispatch('getLicenceScopes')
    },

    async openNewLicenceDialog () {
      this.showNewLicenceDialog = true
      await this.$store.dispatch('getLicenceScopes')
    },

    closeSheet () { this.showLicenceScopeDialog = false }
  }
})
</script>

<style scoped>
.lc-name {
  font-size: .8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  color: var(--ec-text-1);
  background: var(--ec-gray-bg);
  padding: 2px 7px;
  border-radius: 4px;
}
</style>
